/**
 * Centralized API Service
 * Handles all communication with the backend API server
 */

import { log } from '@/utils/logger'

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  rowCount?: number
}

export interface QueryRequest {
  sql: string
  params?: any[]
  schema?: string
}

class ApiService {
  private static instance: ApiService
  private baseUrl: string
  private timeout: number
  private initialized: boolean

  private constructor() {
    // Initialize with same-origin for HTTPS, localhost for HTTP
    const isSecure = window.location.protocol === 'https:'
    this.baseUrl = isSecure ? `${window.location.protocol}//${window.location.host}` : ''
    this.timeout = 10000 // 10 seconds
    this.initialized = false

    // Fetch server configuration in the background - non-blocking
    this.initializeFromServer()
      .then(() => {
        this.initialized = true
        log.api('✅ API Service initialization completed')
      })
      .catch((error) => {
        this.initialized = true // Mark as initialized even on failure
        log.api('⚠️ API Service initialization completed with errors:', error)
      })
  }

  private async initializeFromServer() {
    const currentProtocol = window.location.protocol
    const currentHost = window.location.hostname
    const currentPort = window.location.port
    const isSecure = currentProtocol === 'https:'

    // If accessing via port 9309 (or any non-standard port), we're likely using Docker with Vite proxy
    const isDockerProxy = currentPort === '9309' || (currentPort !== '' && currentPort !== '80' && currentPort !== '443')

    log.api(`🚀 Initializing API Service:`, {
      currentProtocol,
      currentHost,
      currentPort,
      isSecure,
      isDockerProxy,
      currentUrl: window.location.href,
      initialBaseUrl: this.baseUrl
    });

    // For HTTPS sites, Docker proxy, or fortiblox.com, fetch config through the proxy using relative URLs
    if (isSecure || isDockerProxy || currentHost === 'explorer.fortiblox.com') {
      try {
        const configUrl = `${currentProtocol}//${window.location.host}/api/config`;
        log.api(`📡 Fetching config from: ${configUrl}`);

        const configResponse = await fetch(configUrl)
        log.api(`📥 Config response:`, {
          ok: configResponse.ok,
          status: configResponse.status,
          headers: Object.fromEntries(configResponse.headers.entries())
        });

        if (configResponse.ok) {
          const serverConfig = await configResponse.json()
          log.api(`📄 Server config received:`, serverConfig);

          // Force same-origin for HTTPS sites regardless of what the server returns
          const expectedUrl = `${currentProtocol}//${window.location.host}`;
          const receivedUrl = serverConfig.apiUrl;

          if (isSecure && receivedUrl && !receivedUrl.startsWith(`${currentProtocol}//${window.location.host}`)) {
            log.api(`⚠️ Server returned wrong API URL (${receivedUrl}), forcing same-origin: ${expectedUrl}`);
            this.baseUrl = expectedUrl;
          } else {
            // For Docker/proxy setups, server returns empty apiUrl - use it as-is to enable relative URLs
            this.baseUrl = serverConfig.apiUrl !== undefined ? serverConfig.apiUrl : expectedUrl;
          }

          log.api(`✅ Final API base URL: ${this.baseUrl}`);
          log.info(`🔧 HTTPS/Production mode - API Service configured from server: ${this.baseUrl}`)
          log.info(`🔧 Environment: ${serverConfig.environment}`)
          log.info(`🔧 Detected IP: ${serverConfig.detectedIP}`)
          return
        }
      } catch (error) {
        log.api(`❌ Config fetch failed:`, error);
        log.warn(`Failed to fetch server config from HTTPS host, using same-origin fallback`)
      }

      // Fallback to same-origin if config fetch fails
      this.baseUrl = `${currentProtocol}//${window.location.host}`
      log.api(`🔄 Using same-origin fallback: ${this.baseUrl}`);
      log.info(`🔧 HTTPS/Production mode fallback - API Service using same-origin: ${this.baseUrl}`)
      return
    }

    // For local development, try localhost:8547 first
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      try {
        const configResponse = await fetch('/api/config')
        if (configResponse.ok) {
          const serverConfig = await configResponse.json()
          this.baseUrl = serverConfig.apiUrl
          log.info(`🔧 API Service configured from server: ${this.baseUrl}`)
          log.info(`🔧 Environment: ${serverConfig.environment}`)
          log.info(`🔧 Detected IP: ${serverConfig.detectedIP}`)
          return
        }
      } catch (error) {
        log.warn(`Failed to fetch server config from localhost:8547`)
      }
    }

    // Fallback: try current hostname on port 8547
    try {
      const configUrl = `http://${currentHost}:8547/api/config`
      const configResponse = await fetch(configUrl)
      if (configResponse.ok) {
        const serverConfig = await configResponse.json()
        this.baseUrl = serverConfig.apiUrl
        log.info(`🔧 API Service configured from ${currentHost}: ${this.baseUrl}`)
        log.info(`🔧 Environment: ${serverConfig.environment}`)
        log.info(`🔧 Detected IP: ${serverConfig.detectedIP}`)
        return
      }
    } catch (error) {
      log.warn(`Failed to fetch server config from current hostname`)
    }

    // Final fallback: use current hostname with port 8547
    const fallbackUrl = `http://${currentHost}:8547`
    this.baseUrl = fallbackUrl
    log.warn(`🔧 Using fallback API URL: ${this.baseUrl}`)
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService()
    }
    return ApiService.instance
  }

  /**
   * apiService.query() — REMOVED in Wave F (WP #3619, data-director,
   * 2026-05-03).
   *
   * The method dispatched to POST /api/query, which forwarded an arbitrary
   * SQL string from the client to the writer pool. Wave E (WP #3615)
   * retired /api/postgres (the sister raw-SQL endpoint); Wave F retires
   * /api/query and the apiService.query() method itself.
   *
   * Migration map (caller site -> typed endpoint per ADR-0001):
   *   centralizedValidatorService.ts  -> GET /api/validators/locations
   *   web3ValidatorService.ts         -> GET /api/validators/locations
   *   explorerService.ts (locations)  -> GET /api/validators/locations
   *   explorerService.ts (assessment) -> GET /api/validators/assessment/:identity
   *   explorerService.ts (providers)  -> GET /api/validators/assessment-providers
   *   programService.ts (list)        -> GET /api/programs
   *   programService.ts (by id)       -> GET /api/programs/:programId
   *   programService.ts (sub-resources) -> GET /api/programs/:id/{accounts,instructions,logs}
   *   pages/NFTs.tsx                  -> GET /api/nfts/collections
   *   pages/NFTCollection.tsx         -> GET /api/nfts/collection/:collectionId
   *   explorerService.ts (NFT mint)   -> GET /api/nfts/by-mint/:mintAddress
   *   explorerService.ts (NFT list)   -> GET /api/nfts/list
   *   explorerService.ts (rarity)     -> GET /api/nfts/rarity-source/:collectionName
   *   explorerService.ts (token bulk) -> GET /api/tokens/by-mints
   *   explorerService.ts (token list) -> GET /api/tokens/list
   *   explorerService.ts (token det)  -> GET /api/tokens/:mint/details
   *
   * The QueryRequest interface above is also dead; left in place so any
   * code that imported the type doesn't fail to compile until that import
   * is cleaned up by a follow-up.
   *
   * AAAA prevention: .github/workflows/no-raw-sql-endpoint.yml refuses
   * any reintroduction of `app.post('/api/query'` or `app.post('/api/postgres'`
   * raw-SQL handler patterns. The /api/query allowlist entry was removed
   * in Wave F so the gate now refuses BOTH endpoints.
   */

  /**
   * Check if the API server is healthy
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/health`, {
        method: 'GET',

      })
      return response.ok
    } catch {
      return false
    }
  }

  /**
   * Get API server status and connection info
   */
  async getStatus(): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/status`, {
        method: 'GET'
      })

      if (!response.ok) {
        throw new Error(`Status request failed: ${response.status}`)
      }

      const data = await response.json()
      return {
        success: true,
        data
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Update the base URL for the API
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url
  }

  /**
   * Get current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl
  }

  /**
   * Build full API URL - automatically handles proxy routing
   * Use this for ALL API calls to ensure they go through the proxy
   */
  buildUrl(path: string): string {
    // If baseUrl is empty (Docker/proxy mode), use relative URLs
    // Otherwise, use absolute URLs with the baseUrl
    if (this.baseUrl === '') {
      return path.startsWith('/') ? path : `/${path}`
    }

    // For absolute URLs, combine baseUrl with path
    const cleanPath = path.startsWith('/') ? path : `/${path}`
    return `${this.baseUrl}${cleanPath}`
  }

  /**
   * Check if initialization has completed
   */
  isInitialized(): boolean {
    return this.initialized
  }
}

export const apiService = ApiService.getInstance()
export default apiService
